import { z } from 'zod';
import type { IframeWidget } from '.';
import { ZodBaseWidgetConfiguration, ZodGenericWidgetConfigurationDetails } from '../types';
import { WidgetType } from '../types';
import {
    zMarshalToBoolean,
    zMarshalToNumber,
    zMarshalToStrictInt,
} from '../../ReachApi/Marshallers';
import { JsonValue } from '../../Utility/JsonValue';

export const ZodIFrameWidgetConfigurationDetails = ZodGenericWidgetConfigurationDetails.extend({
    url: z.string(),
    x_scroll_offset: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    y_scroll_offset: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    refresh_interval: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    scale: z.union([z.string(), z.number()]).default(1).transform(zMarshalToNumber),
    login_logic: z.string().nullish(),
    username: z.string().nullish(),
    password: z.string().nullish(),
    /**
     * JSON string
     * type: string
     * params: string | undefined
     */
    post_login_actions: z.string().nullish(), // TODO: return the proper "type"
    use_proxy: z.unknown().transform(zMarshalToBoolean), // not used?
    login_sequence: z.string().nullish(),
    windowed: z.unknown().transform(zMarshalToBoolean),
    run_from_zip: z.unknown().transform(zMarshalToBoolean),
    zip_url: z.string().nullish(),
    zip_index_file: z.string().nullish(),
});

export type IFrameWidgetConfigurationDetails = z.infer<typeof ZodIFrameWidgetConfigurationDetails>;

export const ZodIFrameWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    type: z.union([z.literal(WidgetType.IFRAME), z.literal(WidgetType.IFRAME_PREMIUM)]),
    config: ZodIFrameWidgetConfigurationDetails,
});

export type IFrameWidgetConfiguration = z.infer<typeof ZodIFrameWidgetConfiguration>;

export interface LoginLogicData {
    // eslint-disable-next-line
    // "input[placeholder='Username'], input[placeholder='username'], input[placeholder='Email address'], input[placeholder='Email Address'], input[name='Username'], input[name='username'], input[name='emailAddress'], #Username, #username, input[placeholder='User'], input[placeholder='user'], input[name='User'], input[name='user'], #User, #user, input[type='email']",
    username_query: string;
    // eslint-disable-next-line
    // "input[placeholder='Password'], input[placeholder='password'], input[name='Password'], input[name='password'], #Password, #password, input[type='password']"
    password_query: string;
    // eslint-disable-next-line
    // "input[value='Login'], input[value='Sign In'], input[value='sign in'], input[value='Sign in'], input[value='Log in'], input[value='Sign In'], button[type='submit'], input[type='submit'], #passwordNext"
    login_button_query: string;
    //500
    after_body_wait_time: number;
    sign_in_button_query: undefined | string;
    username_continue_button_query: undefined | string;
}

export enum SequenceStepTypes {
    SET_VALUE = 'setValue',
    EXECUTE_ON_ELEMENT = 'executeOnElement',
    EXECUTE_JS = 'executeJS',
    CLICK = 'click',
    WAIT = 'wait',
    WAIT_FOR_ELEMENTS = 'waitForElements',
}

export interface StepSetValue {
    type: SequenceStepTypes.SET_VALUE;
    params: { selector: string; value: string; maxWaitSeconds: undefined | number };
}

export interface StepExecuteOnElement {
    type: SequenceStepTypes.EXECUTE_ON_ELEMENT;
    params: { selector: string; value: string };
}

export interface StepExecuteJs {
    type: SequenceStepTypes.EXECUTE_JS;
    params: { value: string };
}

export interface StepClick {
    type: SequenceStepTypes.CLICK;
    params: { selector: string; maxWaitSeconds: undefined | number };
}

export interface StepWait {
    type: SequenceStepTypes.WAIT;
    params: { timeInSeconds: number; selector: undefined | string; value: undefined | string };
}

export interface StepWaitForElements {
    type: SequenceStepTypes.WAIT_FOR_ELEMENTS;
    params: { elements: string[]; maxWaitSeconds: number };
}

/**
 * An example I found...
 * @TODO: I'm not sure how to define the `steps` in the items...
 */
export interface SequenceConfig {
    login: {
        loginLogic: string;
        loginSteps: undefined | string;
        steps:
            | undefined
            | (
                  | StepSetValue
                  | StepExecuteOnElement
                  | StepExecuteJs
                  | StepClick
                  | StepWait
                  | StepWaitForElements
              )[];
        verification: undefined | LoginVerification;
    };
    postLogin: {
        steps: (
            | StepSetValue
            | StepExecuteOnElement
            | StepExecuteJs
            | StepClick
            | StepWait
            | StepWaitForElements
        )[];
    };
    preLogin: {
        steps: (
            | StepSetValue
            | StepExecuteOnElement
            | StepExecuteJs
            | StepClick
            | StepWait
            | StepWaitForElements
        )[];

        verification: LoginVerification;
    };
}

export interface LoginVerification {
    type: string;
    params: {
        elements: string[];
        maxWaitSeconds?: number;
    };
}

export interface BaseIframeWidget {
    /**
     * Iframe widget object
     */
    widget: IframeWidget;

    /**
     * Draws the iframe window onto the UI
     */
    render(): Promise<void> | void;

    /**
     * removes the iframe window from the UI
     */
    destroy(): Promise<void>;

    /**
     * executes the given JS on the Iframe
     * @param executeInline is deprecated and should be removed.
     * @returns JSON value on success or an string starting with '[ERROR]' on failure
     */
    executeJS(jsToExec: string, executeInline?: boolean): Promise<JsonValue> | JsonValue;

    /**
     * Attempt to load iframe
     */
    attemptIframeLoad(): Promise<void>;
}
